/**
 * Sizing a chart to its pane instead of to the renderer's defaults.
 *
 * `ChartsRenderer` merges fixed pixel sizes into every chart it draws — `height: 350`, plus
 * `width: 350` and `outerRadius: 150` for pie. Those suit a chart standing alone on a page.
 * Here it has a pane, so they leave a small chart adrift at the top of a tall box.
 *
 * A chart with no `height` measures its container instead, so dropping the prop is what lets
 * it fill the pane and follow a window resize or a layout switch.
 *
 * Only untouched values are dropped. `configuration` holds what the user actually changed —
 * the charts panel starts it empty and writes a key per edit — so a height they typed wins.
 */

/** The chart controls this module reads, as the charts panel writes them. */
export type ChartConfiguration = Record<string, string | number | boolean | null> | undefined;

/**
 * How much of the available radius a filled pie takes.
 *
 * Short of 1 on purpose: at the full radius the arcs meet the pane's padding and any arc
 * labels are clipped.
 */
const PIE_FILL_RATIO = 0.9;

/**
 * The renderer's own `outerRadius` default.
 *
 * It resolves each series' radii from this before handing the props over, so it is the scale
 * those pixel values are a fraction of.
 */
const RENDERER_PIE_OUTER_RADIUS = 150;

/** A pie radius as a share of whatever radius the pane affords. */
function asAvailableRadius(radius: unknown): unknown {
    if (typeof radius !== 'number' || !Number.isFinite(radius) || radius < 0) return radius;

    const share = (radius / RENDERER_PIE_OUTER_RADIUS) * PIE_FILL_RATIO * 100;
    return `${Math.round(share * 10) / 10}%`;
}

/**
 * The chart props with the renderer's default sizing removed, so the chart fills its pane.
 *
 * Takes the props the renderer built and the configuration behind them — the second is the
 * only way to tell a default from the same number typed by the user.
 */
export function fillPaneSizing(
    chartType: string,
    props: Record<string, any>,
    configuration: ChartConfiguration
): Record<string, any> {
    const fillHeight = configuration?.height === undefined;
    // Only pie is given a width; the cartesian types already take theirs from the container.
    const fillWidth = chartType === 'pie' && configuration?.width === undefined;

    if (!fillHeight && !fillWidth) return props;

    const sized: Record<string, any> = { ...props };
    if (fillHeight) delete sized.height;
    if (fillWidth) delete sized.width;

    if (chartType !== 'pie') return sized;

    // The legend is a scroll box capped at the width and height just dropped. Re-cap it
    // against the container, or a long legend grows unbounded and squeezes out the pie.
    const legend = sized.slotProps?.legend;
    if (legend?.sx) {
        sized.slotProps = {
            ...sized.slotProps,
            legend: {
                ...legend,
                sx: {
                    ...legend.sx,
                    ...(fillWidth ? { maxWidth: '100%' } : {}),
                    ...(fillHeight ? { maxHeight: '100%' } : {}),
                },
            },
        };
    }

    // Radii arrive resolved per series — inner and outer for each ring, so multi-series pies
    // nest — in pixels against the 150 above. As percentages the pie resolves them against
    // the radius it actually has.
    if (configuration?.outerRadius === undefined && Array.isArray(sized.series)) {
        sized.series = sized.series.map((series: Record<string, any>) => ({
            ...series,
            innerRadius: asAvailableRadius(series.innerRadius),
            outerRadius: asAvailableRadius(series.outerRadius),
        }));
    }

    return sized;
}
